import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import {
	Building2,
	Calendar,
	ExternalLink,
	FileText,
	Loader2,
	Search,
} from "lucide-react";
import { useState } from "react";
import { useTRPC } from "@/integrations/trpc/react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { getStatusColor, getStatusLabel, getEffectiveStatus } from "@/lib/utils";

// Local solicitation type for public portal
interface PublicSolicitation {
	id: string;
	title: string;
	description: string;
	type: string;
	status: string;
	dueDate: string | null;
	publishDate: string | null;
	memberOrg?: string;
	attachmentCount: number;
}

export const Route = createFileRoute("/public/")({
	component: PublicPortalPage,
	validateSearch: (search: Record<string, unknown>) => ({
		buyerId: (search.buyerId as string) ?? "",
	}),
});

function PublicPortalPage() {
	const { buyerId } = useSearch({ from: "/public/" });
	const trpc = useTRPC();
	const [search, setSearch] = useState("");
	const [typeFilter, setTypeFilter] = useState<string>("all");

	// Query public solicitations if buyerId is provided
	const { data: solicitationsData, isLoading } = useQuery(
		trpc.public.solicitations.list.queryOptions({
			buyerOrgId: buyerId,
			limit: 100,
			offset: 0,
		}),
	);

	// Query organization info
	const { data: orgData } = useQuery(
		trpc.public.organizations.get.queryOptions({
			organizationId: buyerId,
		}),
	);

	// Map to local type
	const publicSolicitations: PublicSolicitation[] = (solicitationsData ?? []).map((s) => ({
		id: s.id,
		title: s.title,
		description: s.description ?? "",
		type: s.type,
		status: getEffectiveStatus(s.status, s.publish_date, s.due_date),
		dueDate: s.due_date,
		publishDate: s.publish_date,
		memberOrg: undefined,
		attachmentCount: 0, // TODO: Add attachment count to query
	}));

	const filteredSolicitations = publicSolicitations.filter((sol) => {
		const matchesSearch =
			sol.title.toLowerCase().includes(search.toLowerCase()) ||
			sol.description.toLowerCase().includes(search.toLowerCase());
		const matchesType = typeFilter === "all" || sol.type === typeFilter;
		return matchesSearch && matchesType;
	});

	return (
		<div className="min-h-screen bg-gray-50">
			{/* Header */}
			<header className="bg-white border-b">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="flex items-center justify-between h-16">
						<div className="flex items-center gap-3">
							<div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
								<span className="text-primary-foreground font-bold">B</span>
							</div>
							<div>
								<h1 className="font-semibold text-lg">Bidscope</h1>
								<p className="text-xs text-muted-foreground">Public Portal</p>
							</div>
						</div>
						<div className="flex items-center gap-4">
							{orgData?.slug ? (
								<Button variant="outline" asChild>
									<a href={`https://app.bidscope.ai/purchasing?slug=${orgData.slug}`}>
										Vendor Login
									</a>
								</Button>
							) : (
								<Button variant="outline" asChild>
									<a href="https://app.bidscope.ai/purchasing">
										Vendor Login
									</a>
								</Button>
							)}
						</div>
					</div>
				</div>
			</header>

			{/* Hero */}
			<div className="bg-primary text-primary-foreground py-12">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
					<h1 className="text-4xl font-bold mb-4">{orgData?.name ?? "Public Portal"}</h1>
					<p className="text-xl opacity-90 mb-8">
						Procurement Opportunities Portal
					</p>
					<div className="max-w-2xl mx-auto">
						<div className="relative">
							<Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
							<Input
								placeholder="Search solicitations..."
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								className="pl-12 h-12 text-foreground bg-white"
							/>
						</div>
					</div>
				</div>
			</div>

			{/* Content */}
			<main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
				{/* Filters */}
				<div className="flex flex-col sm:flex-row gap-4 mb-6">
					<Select value={typeFilter} onValueChange={setTypeFilter}>
						<SelectTrigger className="w-[180px]">
							<SelectValue placeholder="All Types" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">All Types</SelectItem>
							<SelectItem value="RFP">RFP</SelectItem>
							<SelectItem value="RFQ">RFQ</SelectItem>
							<SelectItem value="RFI">RFI</SelectItem>
							<SelectItem value="IFB">IFB</SelectItem>
							<SelectItem value="ITB">ITB</SelectItem>
						</SelectContent>
					</Select>
					<p className="text-muted-foreground self-center">
						Showing {filteredSolicitations.length} opportunities
					</p>
				</div>

				{/* Solicitations Grid */}
				{isLoading ? (
					<div className="flex items-center justify-center py-12">
						<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
					</div>
				) : (
				<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
					{filteredSolicitations.map((sol) => (
						<Card key={sol.id} className="hover:shadow-lg transition-shadow">
							<CardHeader>
								<div className="flex items-start justify-between">
									<Badge variant="outline">{sol.type}</Badge>
									<Badge className={getStatusColor(sol.status)}>
										{getStatusLabel(sol.status)}
									</Badge>
								</div>
								<CardTitle className="text-lg mt-2">
									<Link
										to="/public/solicitations/$id"
										params={{ id: sol.id }}
										className="hover:underline"
									>
										{sol.title}
									</Link>
								</CardTitle>
								<CardDescription className="line-clamp-2">
									{sol.description}
								</CardDescription>
							</CardHeader>
							<CardContent>
								<div className="space-y-2 text-sm">
									<div className="flex items-center gap-2 text-muted-foreground">
										<Calendar className="h-4 w-4" />
										<span>
											Due: {new Date(sol.dueDate).toLocaleDateString()}
										</span>
									</div>
									<div className="flex items-center gap-2 text-muted-foreground">
										<Building2 className="h-4 w-4" />
										<span>{sol.memberOrg || "City of Springfield"}</span>
									</div>
									<div className="flex items-center gap-2 text-muted-foreground">
										<FileText className="h-4 w-4" />
										<span>{sol.attachmentCount} documents</span>
									</div>
								</div>
								<Button variant="outline" className="w-full mt-4" asChild>
									<Link to="/public/solicitations/$id" params={{ id: sol.id }}>
										View Details
										<ExternalLink className="ml-2 h-4 w-4" />
									</Link>
								</Button>
							</CardContent>
						</Card>
					))}
				</div>
				)}

				{!isLoading && filteredSolicitations.length === 0 && (
					<div className="text-center py-12">
						<FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
						<h3 className="text-lg font-medium">No solicitations found</h3>
						<p className="text-muted-foreground">
							Try adjusting your search or filters
						</p>
					</div>
				)}
			</main>

			{/* Footer */}
			<footer className="bg-white border-t mt-12">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
					<div className="grid gap-8 md:grid-cols-3">
						<div>
							<h3 className="font-semibold mb-3">{orgData?.name ?? "Organization"}</h3>
							<p className="text-sm text-muted-foreground">
								{orgData?.address ?? "Address not available"}
							</p>
						</div>
						<div>
							<h3 className="font-semibold mb-3">Contact</h3>
							<p className="text-sm text-muted-foreground">
								{orgData?.phone && <>Phone: {orgData.phone}<br /></>}
								{orgData?.email && <>Email: {orgData.email}</>}
								{!orgData?.phone && !orgData?.email && "Contact information not available"}
							</p>
						</div>
						<div>
							<h3 className="font-semibold mb-3">Resources</h3>
							<div className="space-y-1 text-sm">
								<a href="#" className="block text-primary hover:underline">
									Vendor Registration Guide
								</a>
								<a href="#" className="block text-primary hover:underline">
									Bidding Instructions
								</a>
								<a href="#" className="block text-primary hover:underline">
									FAQ
								</a>
							</div>
						</div>
					</div>
					<div className="border-t mt-8 pt-8 text-center text-sm text-muted-foreground">
						Powered by Bidscope - Government Procurement Platform
					</div>
				</div>
			</footer>
		</div>
	);
}
